import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { actions } from 'features/bonusProgram';
import { addNotify } from 'features/notify';

import { levelItems, typeItems, statusItems } from 'features/bonusProgram/data';
import { convertCreateBonusGift } from './convertCreateBonusGift';

const initialState = {
  name: '',
  points: '',
  promocodeOrAmount: '',
  statusItems,
  imageData: {
    image: '',
    ext: '',
    error: null,
  },
  imageUrl: '',
  levelItems,
  typeItems,
}

const useCreateBonusGift = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const actionProcessing = useSelector(s => s.bonusProgram.actionProcessing);
  const currentGifts = useSelector(s => s.bonusProgram.currentGifts.items);
  const locale = useSelector(s => s.locale.locale);
  const id = useParams().id;

  const [state, setState] = useState(initialState);

  const handleNameChange = name => setState(ps => ({ ...ps, name }));
  const handlePointsChange = points => {
    if (/^\d*$/.test(points)) {
      setState(ps => ({ ...ps, points }));
    }
  }

  const handlePromocodeOrAmountChange = promocodeOrAmount => {
    const currentType = state.typeItems.find(v => v.active)?.value;
    if (currentType === 'REAL_BALANCE' && !/^\d*$/.test(promocodeOrAmount)) return;
    setState(ps => ({ ...ps, promocodeOrAmount }));
  }

  const handleStatusChange = statusItems => setState(ps => ({ ...ps, statusItems }));
  const handleImageDataChange = imageData => setState(ps => ({ ...ps, imageData }));

  const handleLevelItemsChange = levelItems => setState(ps => ({ ...ps, levelItems }));
  const handleTypeItemsChange = typeItems => setState(ps => ({
    ...ps,
    typeItems,
    promocodeOrAmount: '',
  }));

  const handleSaveClick = e => {
    e.preventDefault();
    const action = id ? actions.editGift : actions.createGift;
    dispatch(action(convertCreateBonusGift({ id, ...state })));
  };

  const handleCancelClick = () => history.push('/bonus-promo/management');

  useEffect(() => {
    if (id) {
      const gift = currentGifts.find(v => v.id === Number(id));

      if (gift) {
        const actualStatusItems = statusItems.map(v => ({
          ...v,
          active: v.value === gift.active,
        }));

        const actualLevelItems = levelItems.map(v => ({
          ...v,
          active: v.value === gift.level,
        }));

        const actualTypeItems = typeItems.map(v => ({
          ...v,
          active: v.value === gift.type,
        }));

        const promocodeOrAmount = gift.type === 'REAL_BALANCE' ? gift.moneyToBalance : gift.promocode;

        setState({
          name: gift.name,
          points: gift.points,
          promocodeOrAmount,
          statusItems: actualStatusItems,
          imageData: {
            image: '',
            ext: '',
            error: null,
          },
          imageUrl: gift.imageUrl,
          levelItems: actualLevelItems,
          typeItems: actualTypeItems,
        })
      } else {
        history.push('/bonus-promo/management');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const message = locale[state.imageData?.error];
    if (message) dispatch(addNotify(message, 'error'));
  }, [state.imageData]);

  useEffect(() => {
    return () => {
      setState(initialState);
    }
  }, []);

  const isImageViable = (state.imageData.image && state.imageData.ext) || state.imageUrl;
  const disabled = !state.name || !state.points || !state.promocodeOrAmount || actionProcessing || !isImageViable;
  const buttonText = id ? locale.save : locale.create;

  return ({
    values: {
      id,
      actionProcessing,
      state,
      disabled,
      buttonText,
    },
    handlers: {
      handleNameChange,
      handlePointsChange,
      handleStatusChange,
      handleImageDataChange,
      handleLevelItemsChange,
      handleTypeItemsChange,
      handlePromocodeOrAmountChange,
      handleCancelClick,
      handleSaveClick,
    }
  });
};

export default useCreateBonusGift;
